import { ChangeEventHandler, memo, useCallback, useEffect, useMemo, useState } from 'react';

import { MenuItem } from '@material-ui/core';

import { Input } from '../input/Input';
import { Select } from '../select/Select';
import { useStyles } from './ClockTimeInput.styles';
import { ClockTimeInputProps } from './ClockTimeInput.types';
import { convertTo24HourFormat } from './ClockTimeInput.utils';

export const ClockTimeInput = memo(
  ({ value, disabled, error, onChange, is24Format, required }: ClockTimeInputProps) => {
    const styles = useStyles();

    const defaultTime = useMemo(() => {
      if (is24Format) {
        return { value, period: undefined };
      }

      const [hour, minute] = value.split(':').map(num => parseInt(num));

      let period = 'AM';
      let newHour = hour;

      if (hour >= 12) {
        period = 'PM';
        if (hour > 12) {
          newHour = hour - 12; // Convert 13-23 to 1-11
        }
      } else if (hour === 0) {
        newHour = 12; // Convert 00 to 12
        period = 'AM';
      }

      return { value: `${newHour < 10 ? '0' + newHour : newHour}:${minute < 10 ? '0' + minute : minute}`, period };
    }, [is24Format, value]);

    const [time, setTime] = useState(defaultTime);

    const handleTimeChange: ChangeEventHandler<HTMLInputElement> = useCallback(e => {
      setTime(prev => ({ ...prev, value: e.target.value }));
    }, []);

    const handlePeriodChange = useCallback((period: string) => {
      setTime(prev => ({ ...prev, period }));
    }, []);

    useEffect(() => {
      const value = is24Format ? time.value : convertTo24HourFormat(time.value, time.period);

      onChange(value);
    }, [time.period, time.value]);

    return (
      <div className={styles.root}>
        <div className={styles.inputSection}>
          <Input
            name="startTime"
            required={required}
            variant="outlined"
            size="large"
            mask={is24Format ? 'time24' : 'time'}
            disabled={disabled}
            value={time.value}
            onChange={handleTimeChange}
            error={error}
          />
        </div>

        {!is24Format && (
          <div className={styles.inputSection}>
            <Select
              size="large"
              value={time.period}
              onChange={e => {
                handlePeriodChange(e.target.value as string);
              }}
              error={error}
              variant="outlined"
              style={{ padding: '1px 0px' }}
              disabled={disabled}
            >
              <MenuItem value="AM">
                <span>AM</span>
              </MenuItem>

              <MenuItem value="PM">
                <span>PM</span>
              </MenuItem>
            </Select>
          </div>
        )}
      </div>
    );
  },
);
